import api from "./client";
import { BASE_URL } from "./client";
import type { ReposicaoMinimoPorArmazemRow } from "./configuracao";


export interface DashboardStats {
	totalProdutos: number;
	/**
	 * #1694: quanto do `totalProdutos` está reservado para envio Full.
	 *
	 * Está DENTRO do total, não fora: a mercadoria continua no galpão, só
	 * comprometida. O card mostra os dois para ninguém supor que o saldo inteiro
	 * está disponível para vender.
	 */
	unidadesReservadasFull: number;
	produtosEmEstoque: number;
	produtosBaixoEstoque: number;
	produtosSemEstoque: number;
	/** #1577: fluxo — responde ao filtro de período. Era fixo em "hoje". */
	movimentacoes: number;
	/**
	 * #1692: o mesmo total do período, quebrado nos três tipos que viram card.
	 * `conferencia` e `localizacao_zerada` ficam de fora dos cards, então a
	 * soma dos três é menor que `movimentacoes`.
	 */
	movimentacoesPorTipo: {
		entrada: number;
		saida: number;
		transferencia: number;
	};
	separacoesPendentes: number;
	ocorrenciasAbertas: number;
}


export interface EstoquePorArmazem {
	armazem: string;
	quantidade: number;
}


export interface EstoquePorTipoLocalizacao {
	tipo: string;
	quantidade: number;
}


export interface MovimentacoesPorDia {
	dia: string;
	entrada: number;
	saida: number;
	transferencia: number;
}


export interface PedidosPorDia {
	/** Rótulo do balde: "08h", "24/08" ou "08/2026", conforme a granularidade. */
	dia: string;
	/** Pedido sem marcação de separação — inclui o legado EMBALADA. */
	semSeparacao: number;
	aguardandoSeparacao: number;
	emSeparacao: number;
	separada: number;
	cancelada: number;
}

/**
 * #1577: a série deixou de ser 7 baldes fixos de dia-da-semana. O backend
 * decide a granularidade pelo período pedido e devolve só os baldes que existem.
 */
export interface SeriePedidosPorPeriodo {
	granularidade: "hora" | "dia" | "mes";
	inicio: string;
	fim: string;
	/** Soma de TODOS os pontos — mesma base do gráfico, respeita os filtros. */
	total: number;
	pontos: PedidosPorDia[];
}


export interface Top10ProdutoEmEstoque {
	nome: string;
	quantidade: number;
	sku: string;
}


export interface MovimentacaoRecente {
	id: number;
	tipo: string;
	quantidade: number;
	produto: string;
	localizacaoOrigem: string;
	localizacaoDestino: string;
	dataHora: string | Date;
	armazem: string;
}


export interface UsuarioComMaisMovimentacoes {
	nome: string;
	email: string;
	totalMovimentacoes: number;
	entradas: number;
	saidas: number;
	transferencias: number;
}


export interface MovimentacaoCalendario {
	data: string;
	totalMovimentacoes: number;
}


export const buscarEstatisticasDashboard = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
): Promise<DashboardStats> => {
	try {
		// #1577: o período entra por causa de `movimentacoes`, que é fluxo. Os
		// indicadores de saldo do mesmo endpoint não são afetados — a issue os
		// isenta do filtro por serem foto do agora.
		const params: Record<string, string> = {};
		if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;
		const estatisticasRes = await api.get("/dashboard/estatisticas", {
			params,
		});

		const estatisticas: DashboardStats = {
			totalProdutos: estatisticasRes.data.totalProdutos || 0,
			unidadesReservadasFull:
				estatisticasRes.data.unidadesReservadasFull || 0,
			produtosEmEstoque: estatisticasRes.data.produtosEmEstoque || 0,
			produtosBaixoEstoque: estatisticasRes.data.produtosBaixoEstoque || 0,
			produtosSemEstoque: estatisticasRes.data.produtosSemEstoque || 0,
			movimentacoes: estatisticasRes.data.movimentacoes || 0,
			movimentacoesPorTipo: {
				entrada: estatisticasRes.data.movimentacoesPorTipo?.entrada || 0,
				saida: estatisticasRes.data.movimentacoesPorTipo?.saida || 0,
				transferencia:
					estatisticasRes.data.movimentacoesPorTipo?.transferencia || 0,
			},
			separacoesPendentes: estatisticasRes.data.separacoesPendentes || 0,
			ocorrenciasAbertas: estatisticasRes.data.ocorrenciasAbertas || 0,
		};

		return estatisticas;
	} catch (err: any) {
		console.error("❌ Erro ao buscar estatísticas do dashboard:", err);
		// #1607: propaga em vez de devolver zeros. O fallback silencioso fazia
		// falha de carga parecer galpão vazio na tela.
		throw err;
	}
};


// Função para buscar estoque por armazém
export const buscarEstoquePorArmazem = async (
	armazemId?: string,
): Promise<EstoquePorArmazem[]> => {
	try {
		const params = armazemId && armazemId !== "todos" ? { armazemId } : {};
		const estoqueRes = await api.get("/dashboard/estoque-por-armazem", {
			params,
		});

		return estoqueRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar estoque por armazém:", err);
		throw err;
	}
};


// Função para buscar estoque por tipo de localização
export const buscarEstoquePorTipoLocalizacao = async (
	armazemId?: string,
): Promise<EstoquePorTipoLocalizacao[]> => {
	try {
		const estoqueRes = await api.get("/dashboard/estoque-por-tipo-localizacao", {
			params: armazemId ? { armazemId } : {},
		});

		return estoqueRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar estoque por tipo de localização:", err);
		throw err;
	}
};


// Função para buscar movimentações por dia da semana
export const buscarMovimentacoesPorDia = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
): Promise<MovimentacoesPorDia[]> => {
	try {
		const params: any = {};
		if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;
		const movimentacoesRes = await api.get("/dashboard/movimentacoes-por-dia", {
			params,
		});

		return movimentacoesRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar movimentações por dia:", err);
		throw err;
	}
};


// Função para buscar últimas movimentações
export const buscarMovimentacoesRecentes = async (
	armazemId?: string,
): Promise<MovimentacaoRecente[]> => {
	try {
		const params = armazemId && armazemId !== "todos" ? { armazemId } : {};
		const movimentacoesRes = await api.get(
			"/dashboard/movimentacoes-recentes",
			{ params },
		);

		return movimentacoesRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar últimas movimentações:", err);
		throw err;
	}
};


// Função para buscar top 10 produtos em estoque
export const buscarTop10ProdutosEmEstoque = async (
	armazemId?: string,
	/** #1694: 0 devolve a lista inteira; ausente = top 10. */
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
	/**
	 * Tira do saldo o que está em localização de ENVIO — já separado e em caixa
	 * esperando despacho. O Dashboard de Estoque pede assim, para a lista bater
	 * com o card que a abriu; a home continua com a leitura dela.
	 */
	semEnvio?: boolean,
): Promise<Top10ProdutoEmEstoque[]> => {
	try {
		const params: Record<string, string> = {};
		if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
		if (limite !== undefined) params.limite = String(limite);
		if (sku?.trim()) params.sku = sku.trim();
		if (semEnvio) params.semEnvio = "true";
		aplicarCatalogo(params, catalogo);
		const response = await api.get("/dashboard/top-10-produtos-em-estoque", {
			params,
		});
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar top 10 produtos em estoque:", error);
		throw error;
	}
};


// Função para buscar usuários com mais movimentações
export const buscarUsuariosComMaisMovimentacoes = async (
	dataInicio?: Date,
	dataFim?: Date,
	armazemId?: string,
): Promise<UsuarioComMaisMovimentacoes[]> => {
	try {
		let url = "/dashboard/usuarios-com-mais-movimentacoes";
		const params: any = {};

		if (dataInicio && dataFim) {
			params.dataInicio = dataInicio.toISOString();
			params.dataFim = dataFim.toISOString();
		}

		if (armazemId && armazemId !== "todos") {
			params.armazemId = armazemId;
		}

		const usuariosRes = await api.get(url, { params });

		return usuariosRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar usuários com mais movimentações:", err);
		throw err;
	}
};


// Função para buscar movimentações por dia para o calendário
export const buscarMovimentacoesCalendario = async (
	dataInicio: Date,
	dataFim: Date,
	armazemId?: string,
): Promise<MovimentacaoCalendario[]> => {
	try {
		const params: any = {
			dataInicio: dataInicio.toISOString(),
			dataFim: dataFim.toISOString(),
		};

		if (armazemId && armazemId !== "todos") {
			params.armazemId = armazemId;
		}

		const movimentacoesRes = await api.get(
			"/dashboard/movimentacoes-por-dia-calendario",
			{ params },
		);

		return movimentacoesRes.data || [];
	} catch (err: any) {
		console.error("❌ Erro ao buscar movimentações para calendário:", err);
		throw err;
	}
};


// Função de teste para verificar se a API está funcionando
export const testarAPI = async () => {
	try {
		// Teste 1: Health check
		const healthRes = await api.get("/health");

		// Teste 2: Produtos
		const produtosRes = await api.get("/produto");

		// Teste 3: Produto estoque
		const estoqueRes = await api.get("/produto-estoque");

		return {
			health: healthRes.data,
			produtos: produtosRes.data,
			estoque: estoqueRes.data,
		};
	} catch (error: any) {
		console.error("❌ Erro no teste da API:", error);
		console.error("❌ Status:", error.response?.status);
		console.error("❌ Mensagem:", error.response?.data || error.message);
		throw error;
	}
};


export interface EstatisticasTarefas {
	totalTarefas: number;
	tarefasPendentes: number;
	tarefasEmAndamento: number;
	tarefasConcluidasNoPeriodo: number;
	tarefasPorStatus: Array<{
		status: string;
		quantidade: number;
	}>;
	tarefasPorTipo: Array<{
		acao: string;
		quantidade: number;
	}>;
	/**
	 * #1692: só as tarefas PENDENTES e EM ANDAMENTO, quebradas por ação.
	 * `tarefasPorTipo` acima conta o histórico do período, não a fila aberta.
	 */
	tarefasEmAbertoPorTipo: Array<{
		acao: string;
		quantidade: number;
	}>;
}


export interface EstatisticasPedidos {
	totalPedidos: number;
	pedidosAguardandoSeparacao: number;
	pedidosEmSeparacao: number;
	pedidosSeparadosNoPeriodo: number;
	totalSeparados: number;
	valorTotal: number;
	/** #1577: sem os cancelados — eles têm balde próprio abaixo. */
	pedidosPorStatusSeparacao: Array<{
		status: number;
		quantidade: number;
		valorTotal: number;
	}>;
	/**
	 * #1577: pedido cancelado tem o `status_separacao` congelado no momento do
	 * cancelamento. Fica fora dos status de separação para não inflar a fila.
	 */
	/**
	 * #1692: canais de venda da fila de separação, do maior para o menor. A
	 * tarefa não serve para este recorte — `separacao full` nunca tem canal
	 * preenchido (0 de 615 em Minas, 0 de 12.939 em SP).
	 */
	aguardandoPorCanal: Array<{
		canal: string;
		quantidade: number;
	}>;
	pedidosCancelados: {
		quantidade: number;
		valorTotal: number;
	};
}


export interface EstatisticasFull {
	totalFull: number;
	/**
	 * #1577: era `fullsHoje`, que repetia `totalFull` e nunca foi "hoje".
	 * Regra da Ana em 26/08: Fulls com tarefa de separação ainda aberta.
	 */
	fullsEmAndamento: number;
	fullsFaturados: number;
	fullsNaoFaturados: number;
	fullsPorFaturamento: Array<{
		faturado: boolean;
		quantidade: number;
	}>;
	fullsPorVendedor: Array<{
		vendedor: string;
		quantidade: number;
	}>;
}


export const buscarEstatisticasTarefas = async (
	dataInicio?: string,
	dataFim?: string,
	canalVendaId?: string,
): Promise<EstatisticasTarefas> => {
	try {
		const params: any = {};
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;
		if (canalVendaId) params.canalVendaId = canalVendaId;

		const response = await api.get("/dashboard/estatisticas-tarefas", {
			params,
		});
		return response.data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar estatísticas de tarefas:", error);
		throw error;
	}
};


export const buscarEstatisticasPedidos = async (
	canalVendaId?: string,
	dataInicio?: string,
	dataFim?: string,
): Promise<EstatisticasPedidos> => {
	try {
		const params: any = {};
		if (canalVendaId && canalVendaId !== "todos")
			params.canalVendaId = canalVendaId;
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;

		const response = await api.get("/dashboard/estatisticas-pedidos", {
			params,
		});
		return response.data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar estatísticas de pedidos:", error);
		throw error;
	}
};


export const buscarEstatisticasFull = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<EstatisticasFull> => {
	try {
		const params: any = {};
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;

		const response = await api.get("/dashboard/estatisticas-full", { params });
		return response.data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar estatísticas de full:", error);
		throw error;
	}
};


export const buscarPedidosPorDia = async (
	dataInicio?: string,
	dataFim?: string,
	canalVendaId?: string,
): Promise<SeriePedidosPorPeriodo> => {
	try {
		const params: any = {};
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;
		if (canalVendaId) params.canalVendaId = canalVendaId;

		const response = await api.get("/dashboard/pedidos-por-dia", { params });
		const dados = response.data;
		// Tolera a resposta antiga (array puro) para não quebrar se um backend
		// desatualizado responder durante um deploy parcial.
		if (Array.isArray(dados)) {
			return {
				granularidade: "dia",
				inicio: "",
				fim: "",
				total: 0,
				pontos: dados,
			};
		}
		return {
			granularidade: dados?.granularidade ?? "dia",
			inicio: dados?.inicio ?? "",
			fim: dados?.fim ?? "",
			total: dados?.total ?? 0,
			pontos: dados?.pontos ?? [],
		};
	} catch (error: any) {
		console.error("❌ Erro ao buscar pedidos por dia:", error);
		throw error;
	}
};


export interface VendasPorEstado {
	estado: string;
	quantidade: number;
	valorTotal: number;
}


export const buscarVendasPorEstado = async (
	dataInicio?: string,
	dataFim?: string,
	canalVendaId?: string,
): Promise<VendasPorEstado[]> => {
	try {
		const params: any = {};
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;
		if (canalVendaId) params.canalVendaId = canalVendaId;

		const response = await api.get("/dashboard/vendas-por-estado", { params });
		return response.data || [];
	} catch (error: any) {
		console.error("❌ Erro ao buscar vendas por estado:", error);
		throw error;
	}
};


export interface ProdutoPorEstado {
	produto_id: number;
	sku: string;
	descricao: string;
	ean: string | null;
	quantidade_total: number;
	total_pedidos: number;
	valor_total: number;
}


export interface ProdutosPorEstadoResponse {
	estado: string;
	total_produtos: number;
	produtos: ProdutoPorEstado[];
}


export const buscarProdutosPorEstado = async (
	estado: string,
	dataInicio?: string,
	dataFim?: string,
): Promise<ProdutosPorEstadoResponse> => {
	try {
		const params: any = { estado: estado.toUpperCase().trim() };
		if (dataInicio) params.dataInicio = dataInicio;
		if (dataFim) params.dataFim = dataFim;

		const response = await api.get("/dashboard/produtos-por-estado", {
			params,
		});
		return response.data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar produtos por estado:", error);
		throw error;
	}
};


// ==================== MÉTRICAS / PARÂMETROS DO SISTEMA ====================

export interface EstoquePorArmazemRow {
	armazem_id: number;
	armazem_nome: string;
	produtos_distintos: number;
	total_quantidade: number;
	total_reservado: number;
	total_disponivel: number;
}


export interface SystemMetricsOverview {
	gerado_em: string;
	estoque_por_armazem: EstoquePorArmazemRow[];
	pedidos_separacao: {
		aprovados: number;
		aguardando_separacao: number;
		em_separacao: number;
		separados: number;
	};
	tarefas_separacao: {
		pendentes: number;
		em_andamento: number;
	};
}


export const buscarSystemMetricsOverview =
	async (): Promise<SystemMetricsOverview> => {
		const response = await api.get("/system-metrics/overview");
		return response.data;
	};


export const buscarReposicaoMinimosPorArmazem = async (): Promise<
	ReposicaoMinimoPorArmazemRow[]
> => {
	const response = await api.get("/system-settings/reposicao-minimos");
	return response.data;
};


// ==================== RELATÓRIO DE PEDIDOS EXPEDIDOS ====================

export interface RelatorioPedidoExpedido {
	pedidoId: number;
	numPedido: string;
	/** Nº do pedido no marketplace/e-commerce (Amazon, Shopee...). */
	numPedidoEcommerce: string | null;
	/** Data de expedição = data de faturamento/pedido (ISO). */
	dataExpedicao: string;
	canal: string | null;
	unidades: number;
}


export interface RelatorioPedidosExpedidosResumoCanal {
	canal: string;
	pedidos: number;
	unidades: number;
}


/** #1480 — uma faixa horária do ritmo de expedição. */
export interface RelatorioPedidosExpedidosFaixaHora {
	hora: number;
	/** Totais do período — a tela mostra as médias, estes servem para conferência. */
	pedidos: number;
	tarefas: number;
	unidades: number;
	/** Médias por dia com movimento — o que a tabela exibe. */
	mediaPedidos: number;
	mediaTarefas: number;
	mediaUnidades: number;
	mediaOperadores: number;
}


/**
 * #1480 — ritmo por faixa horária, derivado do encerramento da última tarefa do
 * pedido (conclusão da separação, não despacho físico — não existe esse timestamp).
 */
export interface RelatorioPedidosExpedidosPorHora {
	faixas: RelatorioPedidosExpedidosFaixaHora[];
	/** Denominador da média: dias em que houve expedição, não dias corridos do filtro. */
	diasComExpedicao: number;
	/** Pedidos sem tarefa encerrada — ficam fora das faixas. Resíduo (medido: 0 a 0,04%). */
	semHorario: number;
	/** Hora atual quando o filtro alcança hoje: a faixa está incompleta. */
	horaEmCurso: number | null;
}


export interface RelatorioPedidosExpedidosResumo {
	totalPedidos: number;
	totalUnidades: number;
	canais: RelatorioPedidosExpedidosResumoCanal[];
	/** Opcional: backend anterior ao #1480 não devolve este campo. */
	porHora?: RelatorioPedidosExpedidosPorHora;
}


export interface RelatorioPedidosExpedidosResponse {
	results: RelatorioPedidoExpedido[];
	total: number;
	resumo: RelatorioPedidosExpedidosResumo;
}


export const buscarRelatorioPedidosExpedidos = async (params: {
	dataInicio: string;
	dataFim: string;
	canalVenda?: number[];
	search?: string;
	offset?: number;
	limit?: number;
	sortDirection?: "asc" | "desc";
	signal?: AbortSignal;
}): Promise<RelatorioPedidosExpedidosResponse> => {
	const queryParams: Record<string, any> = {
		dataInicio: params.dataInicio,
		dataFim: params.dataFim,
	};
	if (params.canalVenda && params.canalVenda.length)
		queryParams.canal_venda = params.canalVenda.join(",");
	if (params.search) queryParams.search = params.search;
	if (params.offset !== undefined) queryParams.offset = params.offset;
	if (params.limit !== undefined) queryParams.limit = params.limit;
	if (params.sortDirection) queryParams.sort_direction = params.sortDirection;

	const response = await api.get("/relatorio/pedidos-expedidos", {
		params: queryParams,
		signal: params.signal,
	});
	return response.data;
};


// ==================== OCUPAÇÃO DE LOCALIZAÇÕES (issue #1351) ====================

export interface OcupacaoResumoTipo {
	tipo: string;
	ocupadas: number;
	total: number;
}


export interface OcupacaoResumo {
	data: string | null;
	tipos: OcupacaoResumoTipo[];
	totalOcupadas: number;
	totalLocalizacoes: number;
}


export interface OcupacaoSerieDia {
	data: string;
	total: number;
	porTipo: Record<string, number>;
}


export interface OcupacaoSerie {
	dias: OcupacaoSerieDia[];
	tipos: string[];
}


export interface OcupacaoDetalheItem {
	localizacao_id: number;
	localizacao_nome: string;
	tipo: string;
	quantidade_total: number;
	produtos_distintos: number;
}


export interface OcupacaoDetalhe {
	data: string;
	tipos: string[];
	itens: OcupacaoDetalheItem[];
}


export const buscarOcupacaoResumo = async (
	data?: string,
): Promise<OcupacaoResumo> => {
	const params: Record<string, string> = {};
	if (data) params.data = data;
	const response = await api.get("/relatorio/ocupacao/resumo", { params });
	return response.data;
};


export const buscarOcupacaoSerie = async (filtros?: {
	mes?: string;
	dataInicio?: string;
	dataFim?: string;
}): Promise<OcupacaoSerie> => {
	const params: Record<string, string> = {};
	if (filtros?.mes) params.mes = filtros.mes;
	if (filtros?.dataInicio) params.data_inicio = filtros.dataInicio;
	if (filtros?.dataFim) params.data_fim = filtros.dataFim;
	const response = await api.get("/relatorio/ocupacao/serie", { params });
	return response.data;
};


export const buscarOcupacaoDetalhe = async (
	data: string,
	tipo?: string,
): Promise<OcupacaoDetalhe> => {
	const params: Record<string, string> = { data };
	if (tipo) params.tipo = tipo;
	const response = await api.get("/relatorio/ocupacao/detalhe", { params });
	return response.data;
};



/**
 * #1577 — monitoramento da separação (regra da Ana em 26/08).
 * A origem é a TAREFA, não o status do pedido.
 */
export interface MonitoramentoFull {
	/** Tarefas de separação Full ainda abertas. Conta TAREFA. */
	tarefasPendentes: number;
	/** Fulls distintos com ao menos uma tarefa aberta. Conta FULL. */
	fullsEmAndamento: number;
	fullsConcluidos: number;
	fullsComTarefa: number;
	/** Full que nunca gerou tarefa — separação não iniciada. */
	fullsSemTarefa: number;
	tarefasConcluidasNoPeriodo: number;
	serie: Array<{
		dia: string;
		tarefasConcluidas: number;
		fullsAtendidos: number;
	}>;
}

export interface MonitoramentoSeparacao {
	tarefasPendentes: number;
	tarefasConcluidasNoPeriodo: number;
	serie: Array<{ dia: string; tarefasConcluidas: number }>;
	/** Foto do status ATUAL do pedido — não é histórico. */
	pedidosPorStatus: Array<{ status: string; quantidade: number }>;
}

export const buscarMonitoramentoFull = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<MonitoramentoFull> => {
	const params: any = {};
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	const response = await api.get("/dashboard/monitoramento-full", { params });
	return response.data;
};

export const buscarMonitoramentoSeparacao = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<MonitoramentoSeparacao> => {
	const params: any = {};
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	const response = await api.get("/dashboard/monitoramento-separacao", {
		params,
	});
	return response.data;
};


/**
 * #1577 — indicadores de produto por saída.
 * Agregam `item_movimentacao` das movimentações de `tipo = 'saida'`.
 */
export interface ProdutoMaisSaida {
	nome: string;
	sku: string;
	quantidade: number;
}

export interface ProdutoSemSaida {
	nome: string;
	sku: string;
	/** Saldo atual — o mesmo que decide quem entra na lista, e segue o armazém. */
	quantidade: number;
	/** ISO da última saída; `null` se o produto nunca teve saída. */
	ultimaSaida: string | null;
	/** `null` quando nunca houve saída — a tela mostra "Nunca teve saída". */
	diasSemSaida: number | null;
}

export const buscarProdutosMaisSaida = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	/** #1692: 0 devolve a categoria inteira; ausente = top 10. */
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
): Promise<ProdutoMaisSaida[]> => {
	const params: any = {};
	if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	if (sku?.trim()) params.sku = sku.trim();
	aplicarCatalogo(params, catalogo);
	const response = await api.get("/dashboard/produtos-mais-saida", { params });
	return response.data || [];
};

export const buscarProdutosMaisTempoSemSaida = async (
	armazemId?: string,
	/** #1692: 0 devolve a categoria inteira; ausente = top 10. */
	limite?: number,
): Promise<ProdutoSemSaida[]> => {
	const params: any = {};
	if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
	if (limite !== undefined) params.limite = String(limite);
	const response = await api.get("/dashboard/produtos-mais-tempo-sem-saida", {
		params,
	});
	return response.data || [];
};

/**
 * #1577 — produtos com MENOS saída: os que venderam pouco no período.
 * Quem não vendeu nada aparece em `buscarProdutosMaisTempoSemSaida`.
 */
export const buscarProdutosMenosSaida = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	/** #1692: 0 devolve a categoria inteira; ausente = top 10. */
	limite?: number,
): Promise<ProdutoMaisSaida[]> => {
	const params: any = {};
	if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	const response = await api.get("/dashboard/produtos-menos-saida", { params });
	return response.data || [];
};

/**
 * #1692 — indicadores da home que abrem painel de detalhamento.
 *
 * O nome viaja até o backend, que recusa qualquer outro valor. Manter os dois
 * lados na mesma lista evita o painel silenciosamente vazio por erro de grafia.
 */
export type IndicadorDetalhe =
	| "unidades-estoque"
	| "baixo-estoque"
	| "entradas"
	| "saidas"
	| "transferencias"
	| "pedidos-total"
	| "pedidos-aguardando"
	| "pedidos-em-separacao"
	| "pedidos-separados"
	| "tarefas-pendentes";

export interface DetalheIndicador {
	indicador: IndicadorDetalhe;
	/** O MESMO número do card, vindo da mesma query. */
	total: number;
	/** `unidades` só em "Unidades em Estoque"; os outros contam registros. */
	unidade: "registros" | "unidades";
	/** Quantas linhas existem no total — base do "mostrando X de Y". */
	totalLinhas: number;
	linhas: Array<Record<string, string | number | null>>;
}

/**
 * Registros que compõem o número de um card da home.
 *
 * Recebe o contexto que estava valendo na tela. Datas só devem ser enviadas
 * para os indicadores de fluxo: nos de saldo e de fila o card também não as
 * aplica, e mandá-las aqui faria o painel discordar do número clicado.
 */
export const buscarDetalheIndicador = async (
	indicador: IndicadorDetalhe,
	opcoes: {
		armazemId?: string;
		dataInicio?: string;
		dataFim?: string;
		limite?: number;
		offset?: number;
	} = {},
): Promise<DetalheIndicador> => {
	const params: Record<string, string> = {};
	if (opcoes.armazemId && opcoes.armazemId !== "todos")
		params.armazemId = opcoes.armazemId;
	if (opcoes.dataInicio) params.dataInicio = opcoes.dataInicio;
	if (opcoes.dataFim) params.dataFim = opcoes.dataFim;
	if (opcoes.limite !== undefined) params.limite = String(opcoes.limite);
	if (opcoes.offset !== undefined) params.offset = String(opcoes.offset);

	const resposta = await api.get(`/dashboard/detalhe/${indicador}`, { params });
	return resposta.data;
};

export interface PeriodoDisponivel {
	/** ISO da primeira movimentação, ou null quando não há nenhuma. */
	inicio: string | null;
	fim: string | null;
}

/**
 * #1692 — intervalo real coberto pelas movimentações.
 *
 * Alimenta o aviso ao lado das datas livres do filtro "Todos": não existe
 * limite no que se pode digitar, o limite é o dado que existe.
 */
export const buscarPeriodoDisponivel = async (
	armazemId?: string,
): Promise<PeriodoDisponivel> => {
	const params = armazemId && armazemId !== "todos" ? { armazemId } : {};
	const resposta = await api.get("/dashboard/periodo-disponivel", { params });
	return resposta.data;
};

// ===========================================================================
// #1694 — Dashboard de Estoque
//
// Mesmo contrato dos indicadores do #1577/#1692: armazém, período e `limite`
// (0 devolve a lista inteira, ausente devolve o top 10).
// ===========================================================================

export interface ProdutoEmEstoque {
	nome: string;
	sku: string;
	quantidade: number;
}

/**
 * Produto listado pelo tempo que está sem sair.
 *
 * Tipo próprio, e não campos novos em `ProdutoEmEstoque`: "produtos em maior
 * quantidade" usa a mesma base e NÃO traz estes dois — declará-los lá deixaria
 * o tipo prometendo dado que aquele endpoint não devolve.
 */
export interface ProdutoParado extends ProdutoEmEstoque {
	/** ISO da última saída; `null` se o produto nunca teve saída. */
	ultimaSaida: string | null;
	/** `null` quando nunca houve saída — a tela mostra "Nunca teve saída". */
	diasSemSaida: number | null;
}

export interface ProdutoRiscoRuptura extends ProdutoEmEstoque {
	saida: number;
	/** A saída aberta: separação de pedido e envio para o Full. */
	saidaPedidos: number;
	saidaFull: number;
	/** Última movimentação do produto (qualquer tipo), ISO ou `null`. */
	ultimaMovimentacao: string | null;
	/** Quantos períodos iguais ao filtrado o saldo ainda cobre. */
	cobertura: number;
}

export interface ProdutoEntreguePorNota extends ProdutoEmEstoque {
	notas: number;
}

export interface ProdutoUltimaEntrada {
	nome: string;
	sku: string;
	ultimaEntrada: string | null;
	numeroNota: string | null;
}

/** #1879: recorte de catálogo aceito pelos indicadores do Dashboard de Estoque. */
export interface CatalogoFiltro {
	categoriaId?: string;
	marcaId?: string;
}

/** Acrescenta categoria e marca aos params. "todos" e vazio não viajam. */
const aplicarCatalogo = (
	params: Record<string, string>,
	catalogo?: CatalogoFiltro,
) => {
	if (catalogo?.categoriaId && catalogo.categoriaId !== "todos") {
		params.categoriaId = catalogo.categoriaId;
	}
	if (catalogo?.marcaId && catalogo.marcaId !== "todos") {
		params.marcaId = catalogo.marcaId;
	}
	return params;
};

const paramsEstoque = (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	/** #1694: busca por SKU dentro do indicador — parcial, resolvida no banco. */
	sku?: string,
	catalogo?: CatalogoFiltro,
) => {
	const params: Record<string, string> = {};
	if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	if (sku?.trim()) params.sku = sku.trim();
	return aplicarCatalogo(params, catalogo);
};

export const buscarProdutosMenorQuantidade = async (
	armazemId?: string,
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
): Promise<ProdutoParado[]> => {
	const response = await api.get("/dashboard/produtos-menor-quantidade", {
		params: paramsEstoque(
			armazemId,
			undefined,
			undefined,
			limite,
			sku,
			catalogo,
		),
	});
	return response.data || [];
};

export const buscarRiscoRuptura = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
): Promise<ProdutoRiscoRuptura[]> => {
	const response = await api.get("/dashboard/risco-ruptura", {
		params: paramsEstoque(
			armazemId,
			dataInicio,
			dataFim,
			limite,
			sku,
			catalogo,
		),
	});
	return response.data || [];
};

/** #1694: fluxo entrada × saída no eixo do PRODUTO. */
export interface FluxoProduto {
	sku: string;
	nome: string;
	entrada: number;
	/** Pedidos + Full + avulsas. */
	saida: number;
	saidaPedidos: number;
	saidaFull: number;
	/** Entrada menos saída: positivo acumulou no período, negativo drenou. */
	saldoPeriodo: number;
}

export const buscarFluxoEntradaSaidaProduto = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
): Promise<FluxoProduto[]> => {
	const response = await api.get("/dashboard/fluxo-entrada-saida-produto", {
		params: paramsEstoque(
			armazemId,
			dataInicio,
			dataFim,
			limite,
			sku,
			catalogo,
		),
	});
	return response.data || [];
};

export const buscarEstoqueParado = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	sku?: string,
	catalogo?: CatalogoFiltro,
): Promise<ProdutoParado[]> => {
	const response = await api.get("/dashboard/estoque-parado", {
		params: paramsEstoque(
			armazemId,
			dataInicio,
			dataFim,
			limite,
			sku,
			catalogo,
		),
	});
	return response.data || [];
};

export const buscarProdutosEntreguesPorNota = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	sku?: string,
): Promise<ProdutoEntreguePorNota[]> => {
	const response = await api.get("/dashboard/produtos-entregues-por-nota", {
		params: paramsEstoque(undefined, dataInicio, dataFim, limite, sku),
	});
	return response.data || [];
};

export const buscarUltimaEntradaPorNota = async (
	limite?: number,
	sku?: string,
): Promise<ProdutoUltimaEntrada[]> => {
	const response = await api.get("/dashboard/ultima-entrada-por-nota", {
		params: paramsEstoque(undefined, undefined, undefined, limite, sku),
	});
	return response.data || [];
};

// ===========================================================================
// #1879 — indicadores e séries do Dashboard de Estoque
//
// Tudo sob `/dashboard/estoque/*`. Os cinco aceitam o mesmo recorte da tela
// (período, armazém, categoria e marca), com a exceção declarada da evolução:
// o snapshot de saldo é por localização e não guarda produto.
// ===========================================================================

export interface ResumoEstoque {
	/** Saldo do galpão. Não conta ENVIO: peça em envio já saiu do estoque. */
	estoqueTotal: number;
	/** Parte do saldo que já tem dono (reserva ativa). */
	reservado: number;
	/** O que sobra para vender: saldo menos reserva. */
	disponivel: number;
	skusComSaldo: number;
	riscoRuptura: number;
	estoqueParado: number;
	unidadesEntrada: number;
	unidadesSaida: number;
	/** Parte da saída que foi separação de pedido. */
	saidaPedidos: number;
	/** Parte da saída que foi envio para o Full. */
	saidaFull: number;
	/** Quantas vezes o estoque saiu inteiro no período. */
	giro: number;
	/** Dias que o saldo duraria no ritmo do período; `null` sem saída. */
	coberturaDias: number | null;
}

export type GranularidadeSerie = "dia" | "mes";

export interface PontoFluxoEstoque {
	periodo: string;
	rotulo: string;
	entrada: number;
	/** Tudo que saiu no balde: pedidos + Full + avulsas. */
	saida: number;
	saidaPedidos: number;
	saidaFull: number;
}

export interface PontoEvolucaoEstoque {
	periodo: string;
	rotulo: string;
	pecas: number;
}

export interface DistribuicaoArmazem {
	armazemId: number;
	armazem: string;
	pecas: number;
	skus: number;
}

export interface ClasseAbc {
	classe: "A" | "B" | "C";
	skus: number;
	valor: number;
	percentualValor: number;
}

export interface CurvaAbc {
	valorTotal: number;
	skusClassificados: number;
	/** SKUs com saldo que nunca vieram em nota — ficam fora da curva. */
	semCusto: number;
	classes: ClasseAbc[];
}

/** #1879: recorte da tela de Produto — a busca e a faixa de saldo dela. */
export interface RecorteCatalogoProduto {
	busca?: string;
	quantidadeMin?: string;
	quantidadeMax?: string;
}

export const buscarResumoEstoque = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	catalogo?: CatalogoFiltro,
	recorte?: RecorteCatalogoProduto,
): Promise<ResumoEstoque> => {
	const params = paramsEstoque(
		armazemId,
		dataInicio,
		dataFim,
		undefined,
		undefined,
		catalogo,
	);
	// Só viaja o que foi preenchido: vazio aqui significa "sem recorte", e
	// mandá-lo faria o backend filtrar por string vazia.
	if (recorte?.busca?.trim()) params.busca = recorte.busca.trim();
	if (recorte?.quantidadeMin?.trim()) {
		params.quantidadeMin = recorte.quantidadeMin.trim();
	}
	if (recorte?.quantidadeMax?.trim()) {
		params.quantidadeMax = recorte.quantidadeMax.trim();
	}

	const response = await api.get("/dashboard/estoque/resumo", { params });
	return response.data;
};

export const buscarFluxoSerieEstoque = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	catalogo?: CatalogoFiltro,
): Promise<{
	granularidade: GranularidadeSerie;
	pontos: PontoFluxoEstoque[];
}> => {
	const response = await api.get("/dashboard/estoque/fluxo-serie", {
		params: paramsEstoque(
			armazemId,
			dataInicio,
			dataFim,
			undefined,
			undefined,
			catalogo,
		),
	});
	return response.data;
};

export const buscarEvolucaoEstoque = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
): Promise<{
	granularidade: GranularidadeSerie;
	pontos: PontoEvolucaoEstoque[];
}> => {
	const response = await api.get("/dashboard/estoque/evolucao", {
		params: paramsEstoque(armazemId, dataInicio, dataFim),
	});
	return response.data;
};

export const buscarDistribuicaoPorArmazem = async (
	catalogo?: CatalogoFiltro,
): Promise<DistribuicaoArmazem[]> => {
	const response = await api.get("/dashboard/estoque/distribuicao-armazem", {
		params: aplicarCatalogo({}, catalogo),
	});
	return response.data || [];
};

/** Linha do detalhamento da curva ABC (#1879). */
export interface ProdutoCurvaAbc {
	sku: string;
	nome: string;
	classe: "A" | "B" | "C";
	quantidade: number;
	custoUnitario: number;
	valor: number;
}

/**
 * Produtos de uma classe da curva. `limite = 0` traz a classe inteira, como
 * nos demais indicadores da tela.
 */
export const buscarProdutosCurvaAbc = async (
	classe?: "A" | "B" | "C",
	armazemId?: string,
	catalogo?: CatalogoFiltro,
	limite?: number,
	sku?: string,
): Promise<ProdutoCurvaAbc[]> => {
	const params = paramsEstoque(
		armazemId,
		undefined,
		undefined,
		limite,
		sku,
		catalogo,
	);
	if (classe) params.classe = classe;
	const response = await api.get("/dashboard/estoque/curva-abc/produtos", {
		params,
	});
	return response.data || [];
};

export const buscarCurvaAbc = async (
	armazemId?: string,
	catalogo?: CatalogoFiltro,
): Promise<CurvaAbc> => {
	const response = await api.get("/dashboard/estoque/curva-abc", {
		params: paramsEstoque(
			armazemId,
			undefined,
			undefined,
			undefined,
			undefined,
			catalogo,
		),
	});
	return response.data;
};

// ===========================================================================
// #1694 — Dashboard de Notas Fiscais
//
// Volume vai junto em todo indicador, como a issue exige.
// ===========================================================================

export interface ResumoNotaPorStatus {
	status: string;
	notas: number;
	volumes: number;
	/** #1963: soma do `valor_total` das notas da etapa. */
	valor: number;
}

export interface NotasPorFornecedor {
	cnpj: string;
	fornecedor: string;
	uf: string;
	notas: number;
	volumes: number;
	valor: number;
	/** `null` quando o fornecedor ainda não entregou nada. */
	ultimaEntrega: string | null;
}

export interface NotaAgendada {
	dia: string;
	cnpj: string;
	fornecedor: string;
	notas: number;
	volumes: number;
	valor: number;
}

/** #1963: entrega com data prevista vencida e sem chegada. */
export interface NotaEmAtraso extends NotaAgendada {
	diasEmAtraso: number;
}

/** #1963: um balde (mês ou dia) do gráfico de notas, por data de importação. */
export interface SerieNotas {
	/** Rótulo pronto: `Jan/26` ou `05/03`. */
	periodo: string;
	/** #2077: notas que CHEGARAM no balde (`data_chegada`) — não as importadas. */
	recebidas: number;
	/** #2077: notas cuja conferência TERMINOU no balde (`FIM_CONFERENCIA`). */
	conferidas: number;
	valor: number;
	/**
	 * Dias da entrega até o fim da conferência / o lançamento, em soma e
	 * quantidade — a média do período sai de somas, não de médias.
	 */
	somaDiasConferencia: number;
	qtdConferencia: number;
	somaDiasLancamento: number;
	qtdLancamento: number;
	/** Notas com ocorrência (nelas ou no grupo de vínculo). */
	comOcorrencia: number;
}

/** #1963: fornecedor pelo índice de notas com ocorrência. */
export interface OcorrenciaPorFornecedor {
	cnpj: string;
	fornecedor: string;
	notas: number;
	comOcorrencia: number;
	/** De 0 a 1. */
	indice: number;
}

/** Nota que chegou, nota com data marcada, nota sem data nenhuma. */
export type SituacaoEntrega = "ENTREGUE" | "AGENDADA" | "SEM_PREVISAO";

/**
 * O conjunto que a #1791 exige em TODA listagem de nota — card de status,
 * agendadas e notas do fornecedor mostram as mesmas colunas.
 *
 * A linha é a nota de TRANSFERÊNCIA, que é a base do recebimento no projeto.
 * `notaRemessa` e `notaOrigem` dizem de onde ela veio: antes as duas viravam
 * linha separada e a listagem misturava números de documentos diferentes.
 */
export interface NotaListada {
	notaFiscalId: number;
	numeroNota: string;
	/** Nota de origem, que vem dentro da transferência. */
	notaOrigem: string | null;
	/** Remessa vinculada pelo grupo — é dela que sai o fornecedor de verdade. */
	notaRemessa: string | null;
	fornecedor: string;
	cnpj: string;
	/** Uma nota pode ter produtos de mais de uma marca. */
	marcas: string[];
	status: string;
	dataEmissao: string | null;
	/** `null` quando a nota foi importada mas ainda não chegou na doca. */
	dataChegada: string | null;
	/** Data do agendamento; `null` = não possui agendamento. */
	previsaoEntrega: string | null;
	situacaoEntrega: SituacaoEntrega;
	volumes: number;
	valor: number;
	dataImportacao: string;
}

export interface NotaDoFornecedor extends NotaListada {
	lancadoEm: string | null;
	/** `null` quando chegou mas ainda não foi lançada — diferente de zero dia. */
	diasParaEstoque: number | null;
}

/** #1694 (req 9 e 10): produto comprado de um fornecedor, cruzado com a saída. */
export interface ProdutoDoFornecedor {
	sku: string;
	descricao: string;
	/** Quanto entrou pelas notas desse fornecedor no recorte. */
	comprado: number;
	/** #1791: já chegou (nota com data de chegada). */
	entregue: number;
	/** #1791: ainda vai chegar. */
	aEntregar: number;
	/** Próxima data marcada entre as que faltam; `null` = não agendou. */
	proximaPrevisao: string | null;
	notas: number;
	/** Saída no período — a "movimentação" que a issue pede. */
	saida: number;
}

const paramsNotas = (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
) => {
	const params: Record<string, string> = {};
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	return params;
};

/** `cnpj` recorta os cards pelo fornecedor escolhido (#1791). */
export const buscarResumoNotas = async (
	dataInicio?: string,
	dataFim?: string,
	cnpj?: string,
): Promise<ResumoNotaPorStatus[]> => {
	const params = paramsNotas(dataInicio, dataFim);
	if (cnpj) params.cnpj = cnpj;
	const response = await api.get("/dashboard/notas/resumo", { params });
	return response.data || [];
};

export const buscarNotasPorFornecedor = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<NotasPorFornecedor[]> => {
	const response = await api.get("/dashboard/notas/por-fornecedor", {
		params: paramsNotas(dataInicio, dataFim, limite),
	});
	return response.data || [];
};

/** #1963: `granularidade=dia` nos períodos curtos. */
export const buscarSerieNotas = async (
	dataInicio?: string,
	dataFim?: string,
	granularidade: "dia" | "mes" = "mes",
): Promise<SerieNotas[]> => {
	const params = paramsNotas(dataInicio, dataFim);
	params.granularidade = granularidade;
	const response = await api.get("/dashboard/notas/serie", { params });
	return response.data || [];
};

/** #1963: fornecedores com maior índice de ocorrência no período. */
export const buscarOcorrenciasPorFornecedor = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<OcorrenciaPorFornecedor[]> => {
	const response = await api.get(
		"/dashboard/notas/ocorrencias-por-fornecedor",
		{ params: paramsNotas(dataInicio, dataFim, limite) },
	);
	return response.data || [];
};

/** #1963: sem período de propósito — atraso é pergunta sobre o agora. */
export const buscarNotasEmAtraso = async (
	limite?: number,
): Promise<NotaEmAtraso[]> => {
	const response = await api.get("/dashboard/notas/em-atraso", {
		params: paramsNotas(undefined, undefined, limite),
	});
	return response.data || [];
};

/** #1963: nota com o último evento que é atividade (importação não conta). */
export interface NotaComAtividade extends NotaListada {
	/** Tipo do evento — `FIM_CONFERENCIA`, `CHEGADA`... */
	ultimaAtividade: string;
	ultimaAtividadeEm: string;
}

/** #1963: as notas com atividade mais recente, no período. */
export const buscarUltimasNotas = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<NotaComAtividade[]> => {
	const response = await api.get("/dashboard/notas/ultimas", {
		params: paramsNotas(dataInicio, dataFim, limite),
	});
	return response.data || [];
};

/** Sem período de propósito: a pergunta é sobre o que ainda vai chegar. */
export const buscarNotasAgendadas = async (): Promise<NotaAgendada[]> => {
	const response = await api.get("/dashboard/notas/agendadas");
	return response.data || [];
};

/** #1791 (req 5): as notas por trás de uma linha de "Agendadas para chegar". */
export const buscarNotasAgendadasDoDia = async (
	dia: string,
	cnpj: string,
	// #1963: linha de "Notas em atraso" — só as notas que a linha conta.
	emAtraso = false,
): Promise<NotaListada[]> => {
	const response = await api.get("/dashboard/notas/agendadas/notas", {
		params: emAtraso ? { dia, cnpj, emAtraso: "true" } : { dia, cnpj },
	});
	return response.data || [];
};

/** #1791 (req 1): as notas por trás de um card de status. */
export const buscarNotasPorStatus = async (
	status: string,
	dataInicio?: string,
	dataFim?: string,
	cnpj?: string,
	limite?: number,
): Promise<NotaListada[]> => {
	const params = paramsNotas(dataInicio, dataFim, limite);
	if (cnpj) params.cnpj = cnpj;
	const response = await api.get(
		`/dashboard/notas/por-status/${encodeURIComponent(status)}`,
		{ params },
	);
	return response.data || [];
};

/**
 * Produtos do fornecedor (#1694, requisitos 9 e 10).
 *
 * Sem `numeroNota` é o resumo do fornecedor no período; com `numeroNota` são
 * os produtos daquela nota.
 */
export const buscarProdutosDoFornecedor = async (
	cnpj: string,
	dataInicio?: string,
	dataFim?: string,
	numeroNota?: string,
	limite?: number,
): Promise<ProdutoDoFornecedor[]> => {
	const params = paramsNotas(dataInicio, dataFim, limite);
	if (numeroNota) params.numeroNota = numeroNota;
	const response = await api.get(`/dashboard/notas/produtos/${cnpj}`, {
		params,
	});
	return response.data || [];
};

export const buscarNotasDoFornecedor = async (
	cnpj: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<NotaDoFornecedor[]> => {
	const response = await api.get(
		`/dashboard/notas/do-fornecedor/${encodeURIComponent(cnpj)}`,
		{ params: paramsNotas(dataInicio, dataFim, limite) },
	);
	return response.data || [];
};

// ===========================================================================
// #1694 — Dashboard de Reposição
//
// O período recorta os PEDIDOS. Saldo em estoque é foto do agora e não tem
// recorte possível — mesma regra dos indicadores de saldo do dashboard de
// Estoque.
// ===========================================================================

export interface PedidoSemEstoque {
	numPedido: string;
	data: string;
	canal: string | null;
	statusSeparacao: number;
}

export interface ProdutoForaDePrateleira {
	nome: string;
	sku: string;
	picking: number;
	reserva: number;
}

export interface ProdutoPrateleiraInsuficiente extends ProdutoForaDePrateleira {
	demanda: number;
	pedidos: number;
	/** Quanto dá para resolver movendo da reserva. */
	reporAgora: number;
	/** O que nem a reserva cobre — buraco de verdade. */
	faltaComprar: number;
}

/**
 * Totais da lista INTEIRA, não da página carregada: são o tamanho do problema.
 */
export interface TotaisReposicao {
	produtos: number;
	reporAgora: number;
	faltaComprar: number;
}

export interface PrateleiraInsuficiente {
	linhas: ProdutoPrateleiraInsuficiente[];
	totais: TotaisReposicao;
}

const paramsReposicao = (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
) => {
	const params: Record<string, string> = {};
	if (armazemId && armazemId !== "todos") params.armazemId = armazemId;
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	return params;
};

export const buscarPedidosSemEstoque = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<PedidoSemEstoque[]> => {
	const response = await api.get("/dashboard/reposicao/pedidos-sem-estoque", {
		params: paramsReposicao(undefined, dataInicio, dataFim, limite),
	});
	return response.data || [];
};

export const buscarForaDePrateleira = async (
	armazemId?: string,
	limite?: number,
): Promise<ProdutoForaDePrateleira[]> => {
	const response = await api.get("/dashboard/reposicao/fora-de-prateleira", {
		params: paramsReposicao(armazemId, undefined, undefined, limite),
	});
	return response.data || [];
};

export const buscarPrateleiraInsuficiente = async (
	armazemId?: string,
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<PrateleiraInsuficiente> => {
	const response = await api.get(
		"/dashboard/reposicao/prateleira-insuficiente",
		{ params: paramsReposicao(armazemId, dataInicio, dataFim, limite) },
	);
	return (
		response.data || {
			linhas: [],
			totais: { produtos: 0, reporAgora: 0, faltaComprar: 0 },
		}
	);
};

// ===========================================================================
// #1694 — Dashboard de Pedidos
// ===========================================================================

export interface PedidosPorCanal {
	canal: string;
	pedidos: number;
	valor: number;
}

export interface ResumoSeparacao {
	etapas: Array<{ status: number; pedidos: number }>;
	comOcorrencia: number;
}

export interface ProdutoMaisPedido {
	nome: string;
	sku: string;
	pedidos: number;
	unidades: number;
}

export interface PedidosPorHora {
	hora: number;
	pedidos: number;
}

export interface FluxoTarefas {
	estados: Array<{ status: string; tarefas: number }>;
	concluidasPorDia: Array<{ dia: string; tarefas: number }>;
	/** Tarefas concluídas por dia, em média. Dia sem conclusão não entra. */
	media: number;
	pico: number;
}

const paramsPedidos = (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
) => {
	const params: Record<string, string> = {};
	if (dataInicio) params.dataInicio = dataInicio;
	if (dataFim) params.dataFim = dataFim;
	if (limite !== undefined) params.limite = String(limite);
	return params;
};

export const buscarPedidosPorCanal = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<PedidosPorCanal[]> => {
	const response = await api.get("/dashboard/pedidos/por-canal", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return response.data || [];
};

export const buscarResumoSeparacao = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<ResumoSeparacao> => {
	const response = await api.get("/dashboard/pedidos/resumo-separacao", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return response.data || { etapas: [], comOcorrencia: 0 };
};

export const buscarProdutosMaisPedidos = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
): Promise<ProdutoMaisPedido[]> => {
	const response = await api.get("/dashboard/pedidos/produtos-mais-pedidos", {
		params: paramsPedidos(dataInicio, dataFim, limite),
	});
	return response.data || [];
};

export const buscarPedidosPorHora = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<PedidosPorHora[]> => {
	const response = await api.get("/dashboard/pedidos/por-hora", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return response.data || [];
};

export const buscarFluxoTarefas = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<FluxoTarefas> => {
	const response = await api.get("/dashboard/pedidos/fluxo-tarefas", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return (
		response.data || { estados: [], concluidasPorDia: [], media: 0, pico: 0 }
	);
};

// --- Dashboard de Full (#1694) ----------------------------------------------

/** Os três canais que a tela separa. `outros` são os Fulls gravados com nome de pessoa. */
export type CanalFull = "ml" | "shopee" | "outros";

export interface ContagemTarefasFull {
	pendentes: number;
	emAndamento: number;
	concluidas: number;
	canceladas: number;
}

/**
 * #1789: situação do envio lida pelas tarefas do período. `faturado` e
 * `cancelado` são do próprio Full e prevalecem.
 */
export type SituacaoEnvioFull =
	| "em_separacao"
	| "separado"
	| "so_canceladas"
	| "faturado"
	| "cancelado";

export interface EnvioComTarefasFull extends ContagemTarefasFull {
	fullId: number;
	numEnvio: string;
	canal: CanalFull;
	rotulo: string;
	situacao: SituacaoEnvioFull;
}

export interface TarefasPorEnvioFull {
	envios: EnvioComTarefasFull[];
	/** Tarefas que não chegam ao Full pelo caminho das reservas. */
	semEnvio: ContagemTarefasFull;
}

/**
 * Fulls INCLUÍDOS no período, por canal. #1789: "em andamento" saiu daqui —
 * a tela conta pelas tarefas do período (`TarefasPorEnvioFull`).
 */
export interface FullPorCanal {
	canal: CanalFull;
	rotulo: string;
	/** Valores crus de `full.full` no grupo — a tela usa em "Outros vendedores". */
	nomes: string[];
	pendentes: number;
	faturados: number;
	total: number;
}

export interface ProdutoNoFull {
	sku: string;
	descricao: string;
	ml: number;
	shopee: number;
	outros: number;
	total: number;
	envios: number;
}

export interface MediaFluxoPorHora {
	/** Dias do período com alguma tarefa concluída — o divisor da média. */
	dias: number;
	tarefas: number;
	horas: Array<{ hora: number; tarefas: number; media: number }>;
}

export const buscarTarefasPorEnvioFull = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<TarefasPorEnvioFull> => {
	const response = await api.get("/dashboard/full/tarefas-por-envio", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return (
		response.data || {
			envios: [],
			semEnvio: { pendentes: 0, emAndamento: 0, concluidas: 0, canceladas: 0 },
		}
	);
};

export const buscarFullPorCanal = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<FullPorCanal[]> => {
	const response = await api.get("/dashboard/full/por-canal", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return response.data || [];
};

export const buscarTopProdutosFull = async (
	dataInicio?: string,
	dataFim?: string,
	limite?: number,
	sku?: string,
): Promise<ProdutoNoFull[]> => {
	const params = paramsPedidos(dataInicio, dataFim, limite);
	if (sku) params.sku = sku;
	const response = await api.get("/dashboard/full/top-produtos", { params });
	return response.data || [];
};

export const buscarMediaFluxoPorHora = async (
	dataInicio?: string,
	dataFim?: string,
): Promise<MediaFluxoPorHora> => {
	const response = await api.get("/dashboard/full/media-por-hora", {
		params: paramsPedidos(dataInicio, dataFim),
	});
	return response.data || { dias: 0, tarefas: 0, horas: [] };
};
